import type { Optional } from '@festina-life/core'
import { IconButton } from '@festina-life/ui/actions'
import { useTranslate } from '@festina-life/ui/hooks'
import { useCallback, useMemo } from 'react'

import { isAddDynamicFunctionPlaceholder } from '../dynamicFunction/types'
import { isSpecialCasedSubTemplatedValueSetup } from '../specialCasedSubTemplatedViewers/types'
import type { ChildAction, StackableType } from '../stackableTable/types'
import type { TemplateFieldChild } from './TemplateFieldsTable'

export const useRemoveChildAction = (
  isRemoveAllowed: boolean,
): Optional<ChildAction<TemplateFieldChild>> => {
  const translate = useTranslate()
  const handleRemoveChild = useCallback(
    async (childValueSetup: StackableType<TemplateFieldChild>) => {
      if (isAddDynamicFunctionPlaceholder(childValueSetup.value)) {
        throw new Error(
          'The add dynamic function placeholder cannot be deleted.',
        )
      }

      const removedRowValue = childValueSetup.value

      if (isSpecialCasedSubTemplatedValueSetup(removedRowValue)) {
        throw new Error(
          'Removing special cased subtemplated field values is not currently supported',
        )
      }
      await removedRowValue.update(undefined)
    },
    [],
  )

  return useMemo(
    () =>
      isRemoveAllowed
        ? {
            action: handleRemoveChild,
            title: translate('templateViewer.actions.remove'),
            actionId: 'removeChild',
            node: (data) => {
              const canRemoveChild = !data.isEmpty && !data.isInherited

              return canRemoveChild ? (
                <IconButton iconName="MinusCircle" isDanger />
              ) : null
            },
          }
        : null,
    [handleRemoveChild, isRemoveAllowed, translate],
  )
}
